import type { DashboardWidget, DashboardWidgetType, AgentBuilderDashboardSchedule } from './types'

/** Widget types that can be exported in scheduled emails */
export const SCHEDULE_EXPORTABLE_WIDGET_TYPES: DashboardWidgetType[] = [
  'bar',
  'groupedBar',
  'line',
  'donut',
  'table',
  'dataBarTable',
  'pivotTable',
]

export type IncludedWidgetOption = {
  widgetId: string
  title: string
  type: DashboardWidgetType
  disabled: boolean
  disabledReason?: string
}

/**
 * Build the list of widget options for the "Included Widgets" form field.
 * Excludes insight widgets. KPI and gauge widgets are included but marked as disabled.
 */
export function buildIncludedWidgetOptions(widgets: DashboardWidget[]): IncludedWidgetOption[] {
  return widgets
    .filter((w) => w.type !== 'insight')
    .map((w) => {
      const isExportable = SCHEDULE_EXPORTABLE_WIDGET_TYPES.includes(w.type)
      return {
        widgetId: w.id,
        title: w.title || 'Untitled',
        type: w.type,
        disabled: !isExportable,
        disabledReason: !isExportable ? 'Not available in scheduled emails' : undefined,
      }
    })
}

/**
 * Determine which widget ids should be initially selected when opening the modal.
 * - If schedule.includedWidgetIds is null, all exportable widgets are selected.
 * - If it's an array, only the stored ids that exist in the current widgets are selected.
 */
export function initialIncludedWidgetIds(
  schedule: AgentBuilderDashboardSchedule | null,
  widgets: DashboardWidget[],
): string[] {
  const options = buildIncludedWidgetOptions(widgets)
  const allExportableIds = options.filter((o) => !o.disabled).map((o) => o.widgetId)
  const exportableIdSet = new Set(allExportableIds)

  if (!schedule?.includedWidgetIds) {
    return allExportableIds
  }

  return schedule.includedWidgetIds.filter((id) => exportableIdSet.has(id))
}

/**
 * Convert selected widget ids to the payload value for upsert.
 * Returns null when every exportable widget is selected (meaning "all widgets, including future ones").
 * Otherwise returns the exact list of selected ids.
 */
export function toIncludedWidgetIdsPayload(
  selectedIds: string[],
  options: IncludedWidgetOption[],
): string[] | null {
  const exportableOptions = options.filter((o) => !o.disabled)

  const selectedSet = new Set(selectedIds)
  const allExportableSelected = exportableOptions.every((o) => selectedSet.has(o.widgetId))

  return allExportableSelected ? null : selectedIds
}

/**
 * Describe the included widgets for display in the schedule list.
 * Returns "All widgets" when includedWidgetIds is null, or "X of Y widgets" otherwise.
 */
export function describeIncludedWidgets(
  schedule: AgentBuilderDashboardSchedule | null,
  widgets: DashboardWidget[],
): string {
  if (!schedule) return 'All widgets'

  const includedWidgetIds = schedule.includedWidgetIds
  if (includedWidgetIds === null || includedWidgetIds === undefined) {
    return 'All widgets'
  }

  // Ids of widgets since removed from the dashboard are not sent, so they are not counted.
  const exportableIds = buildIncludedWidgetOptions(widgets).filter((o) => !o.disabled).map((o) => o.widgetId)
  const includedCount = exportableIds.filter((id) => includedWidgetIds.includes(id)).length

  return `${includedCount} of ${exportableIds.length} widgets`
}
